import { BehaviorSubject, from, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { first, map, mergeMap, takeLast, takeUntil, tap } from 'rxjs/operators';
import {
  WizardState,
  WizardStateWrapper,
  WizardStepMeta,
  WizardTimeTravelData,
  WizardVersion,
  WizardCurrentStep
} from './interfaces/wizard-step';
import { WizardStateMachineInterface } from './interfaces/wizard-state-machine';
import { WizardStepTracker } from './interfaces/wizard-step-tracker';
import { wrapIntoObservable } from '../shared/utils/rxjs/rxjs';
import { WizardQueryParamHandler } from './interfaces/wizard-query-param-handler';
import type { WizardController } from './interfaces/wizard-controller';
import { WizardStepController } from './interfaces/wizard-step-controller';

export class Wizard {
  private _stepBackSub: Subscription = Subscription.EMPTY;

  private _stepCompleteSub: Subscription = Subscription.EMPTY;

  private _stepTitleSub: Subscription = Subscription.EMPTY;

  private _stepSkipSub: Subscription = Subscription.EMPTY;

  private _stepDirtySub: Subscription = Subscription.EMPTY;

  /**
   * Flag that decides whether the current step will be skipped and
   * removed from step tracker when the next step is resolved.
   */
  private _skipCurrentStep = false;

  private readonly _handleQueryParams?: boolean;

  private _queryParamsChangesListener: VoidFunction | null = null;

  private _destroyed$ = new Subject<void>();

  private readonly _unloadListener;

  private _wizardStateSubject = new BehaviorSubject<WizardStateWrapper>({
    state: WizardState.INIT
  });

  private _currentStepSubject = new BehaviorSubject<WizardCurrentStep | null>(null);

  get currentStepValue() {
    return this._currentStepSubject.getValue();
  }

  set currentStepValue(step: WizardCurrentStep | null) {
    this._currentStepSubject.next(step);
  }

  currentStep$ = this._currentStepSubject.asObservable();

  wizardState$ = this._wizardStateSubject.asObservable();

  constructor(
    private _controller: WizardController,
    private _stateMachine: WizardStateMachineInterface,
    public stepTracker: WizardStepTracker,
    private _queryParamHandler?: WizardQueryParamHandler
  ) {
    this._handleQueryParams = !!_queryParamHandler;

    if (this._handleQueryParams) {
      this._subscribeQueryParams();
    } else {
      this._goToNextStep();
    }

    this._unloadListener = () => {
      this.saveStepBeforeUnload();
    };

    window.addEventListener('beforeunload', this._unloadListener);
  }

  /**
   * Query param handling
   */
  private _getStepPathFromQueryParam(): string | null {
    if (!this._queryParamHandler) {
      return null;
    }

    return this._queryParamHandler.getPath(this._stateMachine.queryParamKey);
  }

  private _updateQueryParam(path: string) {
    if (!this._queryParamHandler) {
      return;
    }

    const { currentStepIndex } = this.stepTracker;
    const replaceUrl = currentStepIndex === -1;

    this._queryParamHandler.updatePath(this._stateMachine.queryParamKey, path, replaceUrl);
  }

  private _subscribeQueryParams() {
    if (!this._queryParamHandler) {
      return;
    }

    this._queryParamsChangesListener = this._queryParamHandler.changes(
      this._stateMachine.queryParamKey,
      () => {
        const intendedPath = this._getStepPathFromQueryParam();

        if (!intendedPath) {
          this._resetAndGotoInitialStep();
        } else {
          const intendedStep = this._stateMachine.getStepFromPath(intendedPath);

          if (!intendedStep) {
            this._resetAndGotoInitialStep();
          } else {
            this._goToStep(intendedStep);
          }
        }
      }
    );
  }

  /**
   * Next step path is returned as an observable, so we wait for the observable
   * to resolve and meanwhile show loading component if specified by state machine
   */
  private _resolveStepPath(
    pathResolver: string | Observable<string> | Promise<string>,
    skipUpdateQueryParams: boolean = false
  ) {
    this._wizardStateSubject.next({ state: WizardState.LOADING_STEP });

    wrapIntoObservable(pathResolver).subscribe(
      (path: string) => {
        if (!path) {
          this._markWizardAsFinished();
          return;
        }

        if (!skipUpdateQueryParams && this._handleQueryParams) {
          this._updateQueryParam(path);
        } else {
          this._resolveStep(path);
        }
      },
      error => {
        this._wizardStateSubject.next({ state: WizardState.ERRORED, error });
      }
    );
  }

  private _resolveStep(path: string) {
    this._selectStep(this._stateMachine.getStepFromPath(path));
  }

  private _selectStep(stepMeta: WizardStepMeta | undefined) {
    if (!stepMeta) {
      return;
    }

    this._runResolvers(stepMeta).subscribe(
      data => {
        /**
         * If next step is final or undefined, notify wizard about completion
         */
        if (stepMeta.isFinal) {
          this._controller.markAsComplete();
        }
        /**
         * Track next step
         */
        this.stepTracker.track(stepMeta, this._skipCurrentStep);
        this._skipCurrentStep = false;
        this.stepTracker.saveStepParams(stepMeta);

        const stepData = this.stepTracker.getStepData(stepMeta.path);

        const controller: WizardStepController = new WizardStepController(
          this,
          stepMeta.params || {},
          data,
          stepData,
          stepMeta.isFinal,
          this.stepTracker.getStepIndexFromPath(stepMeta.path) === 0
        );

        this._wizardStateSubject.next({ state: WizardState.IDLE });

        let component;

        if (typeof stepMeta.resolveComponent === 'function') {
          component = stepMeta.resolveComponent();
        } else {
          component = stepMeta.component;
        }

        if (component) {
          this.currentStepValue = {
            component,
            footerComponent: stepMeta.footerComponent,
            controller,
            path: stepMeta.path
          };
        }

        controller.unsavedData = stepData;

        this._subscribeStepActions();
      },
      error => {
        this._wizardStateSubject.next({ state: WizardState.ERRORED, error });
      }
    );
  }

  /**
   * Returns an observable that resolves step data. While
   * resolving data a loading component can be shown if provided
   * by the state machine
   */
  private _runResolvers(
    stepMeta: WizardStepMeta
  ): Observable<Record<string, string | number | boolean | object>> {
    const resolveData = stepMeta.resolve || {};
    const keys: string[] = Object.keys(resolveData);

    if (!keys.length) {
      return of({});
    }

    const data: Record<string, string | number | object | boolean> = {};

    return from(keys).pipe(
      mergeMap((key: string) => {
        const observableData = resolveData[key];
        const canResolveObservableData =
          'resolve' in observableData && typeof observableData.resolve === 'function';

        return wrapIntoObservable(
          canResolveObservableData
            ? observableData.resolve(this.stepTracker, stepMeta.params)
            : observableData
        ).pipe(
          first(),
          tap(value => {
            data[key] = value;
          })
        );
      }),
      map(() => {
        return data;
      }),
      takeLast(1)
    );
  }

  /**
   * Handle step complete and back actions
   */
  private _subscribeStepActions() {
    this._clearStepActionsSub();

    if (!this.currentStepValue) {
      return;
    }

    this._stepBackSub = merge(
      this.currentStepValue.controller.back,
      this._controller.requestBack
    ).subscribe(() => {
      this._onStepBack();
    });

    this._stepCompleteSub = this.currentStepValue.controller.complete.subscribe(() => {
      this._onStepComplete();
    });

    this._stepTitleSub = this.currentStepValue.controller.title.subscribe((data: string) => {
      this._onStepTitleChange(data);
    });

    this._stepSkipSub = this.currentStepValue.controller.skip.subscribe(() => {
      this._onStepSkip();
    });

    this._stepDirtySub = this.currentStepValue.controller.dirty.subscribe(() => {
      this.stepTracker.markCurrentStepDirty(this.currentStepValue?.path);
    });
  }

  private _clearStepActionsSub() {
    this._stepBackSub.unsubscribe();
    this._stepCompleteSub.unsubscribe();
    this._stepTitleSub.unsubscribe();
    this._stepSkipSub.unsubscribe();
    this._stepDirtySub.unsubscribe();
  }

  private _onStepComplete() {
    /**
     * Save current step data in step tracker
     */
    if (this.currentStepValue) {
      this.stepTracker.saveStepData(
        this.currentStepValue?.path,
        this.currentStepValue?.controller.unsavedData
      );

      this.stepTracker.markStepAsComplete(this.currentStepValue.path);
      this._goToNextStep();
    }
  }

  private _goToStep(step: WizardStepMeta) {
    /**
     * Bypass step validation for old versions
     */
    if (this.stepTracker.version !== WizardVersion.V2) {
      this._selectStep(step);
      return;
    }

    const stepIndex = this.stepTracker.getStepIndexFromPath(step.path);

    const existingSteps = this.stepTracker.getSteps();

    /**
     * if the step already exists in the stack, it can be safely visited.
     */
    if (stepIndex >= 0) {
      this._selectStep(step);
      return;
    }

    /**
     * If an existing step's next step is same as the requested step, it can be safely visited
     */
    if (
      existingSteps.length &&
      existingSteps.find(existingStep => existingStep.next === step.path)
    ) {
      this._selectStep(step);
      return;
    }

    /**
     * As a last resort if the requested step is same as initial step, allow it to be visited
     * without resetting the wizard, else reset the wizard and go to initial step.
     */
    wrapIntoObservable(this._stateMachine.getInitialPath(this.stepTracker))
      .pipe(takeUntil(this._destroyed$))
      .subscribe(initialPath => {
        if (step.path !== initialPath) {
          /**
           * Avoid fetching initial path again, as it was just resolved
           */
          this._resetAndGotoInitialStep(initialPath);
        } else {
          this._selectStep(step);
        }
      });
  }

  private _goToNextStep() {
    let pathResolver: string | Observable<string> | Promise<string>;

    if (this.stepTracker.currentStep) {
      let currentStepPathResolver: string | Observable<string> | Promise<string> | undefined;
      const currentStepRef = this._stateMachine.getStepFromPath(this.stepTracker.currentStep.path);

      if (currentStepRef) {
        if (currentStepRef.nextStep) {
          currentStepPathResolver = currentStepRef.nextStep;
        } else if (typeof currentStepRef.getNextStep === 'function') {
          currentStepPathResolver = currentStepRef.getNextStep(
            this.stepTracker,
            currentStepRef,
            this._skipCurrentStep
          );
        }
      }

      /**
       * Mark the next applicable step before proceeding, to later check the validity of the step that user is trying
       * to access
       */
      if (currentStepPathResolver) {
        pathResolver = wrapIntoObservable(currentStepPathResolver).pipe(
          tap(path => {
            if (this.stepTracker.currentStep) {
              this.stepTracker.currentStep.next = path;
            }
          })
        );
      } else {
        pathResolver = this._stateMachine.getInitialPath(this.stepTracker);
      }
    } else {
      pathResolver = this._stateMachine.getInitialPath(this.stepTracker);
    }

    if (!pathResolver) {
      this._markWizardAsFinished();
      return;
    }

    this._resolveStepPath(pathResolver);
  }

  private _onStepBack() {
    this.saveStepBeforeUnload();

    if (this.currentStepValue) {
      const stepTrackerStep = this.stepTracker.getPrevStep(this.currentStepValue.path);

      if (stepTrackerStep) {
        this._resolveStepPath(stepTrackerStep.path);
      }
    }
  }

  private _onStepSkip() {
    this._skipCurrentStep = true;
    this._goToNextStep();
  }

  private _onStepTitleChange(title: string) {
    if (this.currentStepValue) {
      this.stepTracker.updateStepTitle(this.currentStepValue.path, title);
    }
  }

  private _markWizardAsFinished() {
    this._controller.markAsComplete();

    this.stepTracker.markAsComplete();
  }

  private _resetAndGotoInitialStep(initialPath?: string) {
    this.currentStepValue = null;
    this.stepTracker.reset();

    /**
     * If the initial path is provided as input, no need to fetch it again
     */
    if (initialPath) {
      this._resolveStepPath(initialPath);
    } else {
      this._goToNextStep();
    }
  }

  destroy() {
    this._clearStepActionsSub();
    this._destroyed$.next();
    this._destroyed$.complete();

    if (typeof this._queryParamsChangesListener === 'function') {
      this._queryParamsChangesListener();
    }

    if (this._unloadListener) {
      window.removeEventListener('beforeunload', this._unloadListener);
    }
  }

  saveStepBeforeUnload() {
    if (this.currentStepValue) {
      if (typeof this.currentStepValue.beforeUnload === 'function') {
        this.currentStepValue.beforeUnload();
      }

      this.stepTracker.saveStepData(
        this.currentStepValue.path,
        this.currentStepValue.controller.unsavedData
      );
    }
  }

  goTo(path: string) {
    this.saveStepBeforeUnload();
    this._resolveStepPath(path);
  }

  fastTravel(timeTravelData: WizardTimeTravelData<object>) {
    this.stepTracker.fastTravel(timeTravelData);

    if (!timeTravelData.steps.length) {
      this._goToNextStep();
      return;
    }

    const { path } = timeTravelData.steps[timeTravelData.steps.length - 1];

    if (timeTravelData.next && timeTravelData.steps[timeTravelData.steps.length - 1].complete) {
      this._goToNextStep();
    } else {
      this._resolveStepPath(path, !!timeTravelData?.skipUpdateQueryParams);
    }
  }

  closeWizard() {
    this.currentStepValue?.controller.setCloseWizard();
  }

  getController() {
    return this._controller;
  }
}
